import React, { Component } from "react";

import GoogleMapReact from "google-map-react";

import { Collapse } from "reactstrap";

import {
  secondToMinute,
  secondsToFormateTime,
  formatTime,
  formatRelativeTime,
} from "../helpers/utils";
import { weatherLoadingHtml } from "../containers/FieldApp/fieldAppWeather";
import {
  POINT_DETAILS_INFO_WINDOW_MS,
  pointDetailsInfoWindowHtml,
} from "../containers/FieldApp/pointDetailsInfoWindow";
import { withTranslation } from "react-i18next";

import stoppedIcon from "../assets/icons/stopped.ico";
import offlineIcon from "../assets/icons/offline.ico";

import insideIcon from "../assets/icons/enter.ico";
import outIcon from "../assets/icons/exit.ico";
import myConfig from "../configs/config";
import { LAND_PLOT_MAP_STYLE } from "../utils/mapMeasurements";

import "../containers/Operation/Operation.css";

const DEFAULT_MAP_CENTER = { lat: -15.7801, lng: -47.9292 };

function isValidMapCoordinate(point) {
  if (!point) return false;
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return false;
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

function asMapCoordinate(point) {
  if (!isValidMapCoordinate(point)) return null;
  return { lat: Number(point.lat), lng: Number(point.lng) };
}

function lastValidPointFromLines(lines) {
  const list = Array.isArray(lines) ? lines : [];
  for (let lineIndex = list.length - 1; lineIndex >= 0; lineIndex -= 1) {
    const path = list[lineIndex] && list[lineIndex].path;
    if (!Array.isArray(path)) continue;
    for (let pointIndex = path.length - 1; pointIndex >= 0; pointIndex -= 1) {
      if (isValidMapCoordinate(path[pointIndex])) {
        return path[pointIndex];
      }
    }
  }
  return null;
}

export class MapContainer extends Component {
  resolveMapCenter(map, landplots) {
    const centroid = asMapCoordinate({
      lat: map && map.centroidLat,
      lng: map && map.centroidLng,
    });
    if (centroid) {
      return centroid;
    }

    const lastValid = lastValidPointFromLines((map && map.lines) || []);
    if (lastValid) {
      return { lat: Number(lastValid.lat), lng: Number(lastValid.lng) };
    }

    if (landplots && landplots.length) {
      const lpCenter = asMapCoordinate(landplots[0].landplot_center);
      if (lpCenter) {
        return lpCenter;
      }
    }

    return DEFAULT_MAP_CENTER;
  }

  getEffectiveCenter() {
    const highlight = this.resolveHighlightMapPoint();
    if (highlight) {
      return highlight;
    }
    const propsCenter = this.resolveMapCenter(
      this.props.map,
      this.props.landplots
    );
    const stateCenter = asMapCoordinate(this.state.center);
    if (stateCenter) {
      return stateCenter;
    }
    return propsCenter;
  }

  constructor(props) {
    super(props);
    const initialZoom =
      props.trackingMapZoom != null ? Number(props.trackingMapZoom) : 15;
    this.state = {
      marker: null,
      poly: null,
      center:
        asMapCoordinate(props.highlightMapPoint) ||
        this.resolveMapCenter(props.map, props.landplots),
      mapZoom: Number.isFinite(initialZoom) ? initialZoom : 15,
      collapsePoints: false,
      isActiveStops: false,
      isActiveSpeeds: false,
      isActiveLandplots: false,
      userLocationActive: false,
    };
    this.userLocationMarker = null;
    this.userLocationCircle = null;
    this.watchId = null;
    this.trackingPolylines = [];
    this.landplotPolygons = [];
    this.landplotMarkers = [];
    this.pathInfoWindow = null;
    this.pathInfoWindowCloseTimer = null;
    this.directionMarkerRef = null;
    this.highlightMarkerRef = null;
    this.trackingLinesSignature = "";
  }

  formatTraceTimestamp = (time) => {
    if (typeof this.props.formatTraceTime === "function") {
      return this.props.formatTraceTime(time);
    }
    return formatTime(time);
  };

  getRelativeTime = (timestamp) => {
    return formatRelativeTime(timestamp, this.props.t);
  };

  buildLinesSignature(lines) {
    if (!lines || !lines.length) return "0";
    let pointCount = 0;
    let lastTime = "";
    lines.forEach((line) => {
      const path = line.path || [];
      pointCount += path.length;
      if (path.length) {
        lastTime = String(path[path.length - 1].time || "");
      }
    });
    return `${lines.length}:${pointCount}:${lastTime}`;
  }

  clearTrackingOverlays() {
    (this.trackingPolylines || []).forEach((polyline) => polyline.setMap(null));
    (this.landplotPolygons || []).forEach((polygon) => polygon.setMap(null));
    (this.landplotMarkers || []).forEach((marker) => marker.setMap(null));
    if (this.state.marker) {
      this.state.marker.setMap(null);
      this.state.marker = null;
    }
    if (this.directionMarkerRef) {
      this.directionMarkerRef.setMap(null);
      this.directionMarkerRef = null;
    }
    if (this.highlightMarkerRef) {
      this.highlightMarkerRef.setMap(null);
      this.highlightMarkerRef = null;
    }
    this.trackingPolylines = [];
    this.landplotPolygons = [];
    this.landplotMarkers = [];
  }

  refreshTrackingOverlays(google, { fitBounds = false } = {}) {
    if (!google || !google.map) return;
    this.clearTrackingOverlays();
    this.setFarmLandplots(google);
    this.setPolyline(google, { fitBounds });
    this.trackingLinesSignature = this.buildLinesSignature(
      this.props.map && this.props.map.lines
    );
  }

  resolvePointCourse(google, point, path) {
    if (point.course != null && Number(point.course) !== 0) {
      return Number(point.course);
    }

    const points = path || [];
    let index = -1;
    for (let i = 0; i < points.length; i += 1) {
      if (point.id != null && points[i].id === point.id) {
        index = i;
        break;
      }
      if (String(points[i].time) === String(point.time)) {
        index = i;
        break;
      }
    }

    if (index < 0) return 0;

    const from = index > 0 ? points[index - 1] : points[index];
    const to = index < points.length - 1 ? points[index + 1] : points[index];
    if (!from || !to || (from.lat === to.lat && from.lng === to.lng)) {
      return 0;
    }

    return google.maps.geometry.spherical.computeHeading(
      new google.maps.LatLng(from.lat, from.lng),
      new google.maps.LatLng(to.lat, to.lng)
    );
  }

  showDirectionMarker(google, point, lineColor, path) {
    if (this.directionMarkerRef) {
      this.directionMarkerRef.setMap(null);
    }

    const rotation = this.resolvePointCourse(google, point, path);

    this.directionMarkerRef = new google.maps.Marker({
      icon: {
        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        rotation,
        strokeColor: lineColor || "#007BFF",
        fillColor: lineColor || "#007BFF",
        scale: 3,
        strokeWeight: 3.5,
        fillOpacity: 0.9,
      },
      position: { lat: point.lat, lng: point.lng },
      map: google.map,
      zIndex: 1001,
    });

    return this.directionMarkerRef;
  }

  schedulePathInfoWindowClose = () => {
    if (this.pathInfoWindowCloseTimer) {
      clearTimeout(this.pathInfoWindowCloseTimer);
    }
    this.pathInfoWindowCloseTimer = setTimeout(() => {
      if (this.pathInfoWindow) {
        this.pathInfoWindow.close();
      }
      this.pathInfoWindowCloseTimer = null;
    }, POINT_DETAILS_INFO_WINDOW_MS);
  };

  addPolylineClickListener = (google, polyline, lineData) => {
    if (!this.pathInfoWindow) {
      this.pathInfoWindow = new google.maps.InfoWindow({
        zIndex: 1000,
        maxWidth: 260,
      });
    }

    google.maps.event.addListener(polyline, "click", (event) => {
      const clickedLatLng = event.latLng;

      let closestPoint = null;
      let minDistance = Infinity;

      (lineData.path || []).forEach((point) => {
        const pointLatLng = new google.maps.LatLng(point.lat, point.lng);
        const distance = google.maps.geometry.spherical.computeDistanceBetween(
          clickedLatLng,
          pointLatLng
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = point;
        }
      });

      if (!closestPoint) return;

      if (this.props.onPathPointSelected) {
        this.props.onPathPointSelected(closestPoint);
      }

      const t = this.props.t || ((s) => s);
      const buildContent = (weatherHtml) =>
        pointDetailsInfoWindowHtml({
          t,
          weatherHtml,
          speed: closestPoint.speed,
          timeLabel: this.formatTraceTimestamp(closestPoint.time),
          relativeTime: this.getRelativeTime(closestPoint.time),
          showMore: true,
        });

      const bindObservations = () => {
        if (!this.props.onOpenObservations) return;
        google.maps.event.addListenerOnce(this.pathInfoWindow, "domready", () => {
          const btn = document.getElementById("obs-btn");
          if (btn) {
            btn.addEventListener("click", () => {
              this.props.onOpenObservations({ date: closestPoint.time });
              this.pathInfoWindow.close();
            });
          }
        });
      };

      const applyWeather = (weatherHtml) => {
        this.pathInfoWindow.setContent(buildContent(weatherHtml || ""));
        bindObservations();
      };

      const directionMarker = this.showDirectionMarker(
        google,
        closestPoint,
        lineData.color,
        lineData.path
      );

      const maybeWeather =
        typeof this.props.weatherHtmlForPoint === "function"
          ? this.props.weatherHtmlForPoint(closestPoint)
          : "";

      if (maybeWeather && typeof maybeWeather.then === "function") {
        applyWeather(weatherLoadingHtml(t));
        this.pathInfoWindow.open(google.map, directionMarker);
        this.schedulePathInfoWindowClose();
        maybeWeather.then(applyWeather).catch(() => applyWeather(""));
      } else {
        applyWeather(maybeWeather || "");
        this.pathInfoWindow.open(google.map, directionMarker);
        this.schedulePathInfoWindowClose();
      }
    });
  };

  resolveHighlightMapPoint() {
    return asMapCoordinate(this.props.highlightMapPoint);
  }

  showHighlightMarker(google, point) {
    if (!point || !this.props.equipment) return null;

    if (this.highlightMarkerRef) {
      this.highlightMarkerRef.setMap(null);
    }

    this.highlightMarkerRef = new google.maps.Marker({
      position: point,
      title: this.props.highlightMapPoint && this.props.highlightMapPoint.title,
      icon: require(`../assets/traccar/${this.props.equipment.machine_status}/${this.props.equipment.traccar_ico}.svg`),
      opacity: 1,
      zIndex: 1002,
    });
    this.highlightMarkerRef.setMap(google.map);
    return this.highlightMarkerRef;
  }

  setPolyline = (google, { fitBounds = true } = {}) => {
    let polylines = [];
    let last_posi = null;

    const lines = (this.props.map && this.props.map.lines) || [];
    if (Array.isArray(lines) && lines.length) {
      polylines = lines;
      last_posi = lastValidPointFromLines(polylines);
    }

    const highlightPoint = this.resolveHighlightMapPoint();

    // MARKER PARA LOCALIZAÇÃO CENTRAL
    const live = asMapCoordinate(this.props.livePosition);
    if (live) {
      last_posi = {
        lat: live.lat,
        lng: live.lng,
        speed: (this.props.livePosition && this.props.livePosition.speed) || 0,
      };
    }

    if (highlightPoint) {
      this.showHighlightMarker(google, highlightPoint);
    } else if (isValidMapCoordinate(last_posi) && this.props.equipment) {
      this.state.marker = new google.maps.Marker({
        position: last_posi,
        title: last_posi.speed,
        icon: require(`../assets/traccar/${this.props.equipment.machine_status}/${this.props.equipment.traccar_ico}.svg`),
        opacity: 1,
      });
      this.state.marker.setMap(google.map);
    }

    if (!polylines || !polylines.length) {
      const fallback = highlightPoint || asMapCoordinate(last_posi);
      if (fallback && fitBounds) {
        google.map.setCenter(fallback);
        const zoom = Number(this.props.trackingMapZoom);
        google.map.setZoom(Number.isFinite(zoom) ? zoom : 16);
      }
      return;
    }

    // RENDERIZANDO TODAS AS LINHAS
    const bounds = new google.maps.LatLngBounds();
    if (highlightPoint) {
      bounds.extend(highlightPoint);
    }
    const markerPos = asMapCoordinate(last_posi);
    if (markerPos) {
      bounds.extend(markerPos);
    }
    for (let i = 0; i < polylines.length; i++) {
      const path = (polylines[i].path || []).filter(isValidMapCoordinate);
      if (!path.length) continue;
      const polyline = new google.maps.Polyline({
        path,
        strokeColor: polylines[i].color,
        strokeOpacity: 0.9,
        strokeWeight: 6,
        clickable: true,
        zIndex: 2,
      });
      polyline.setMap(google.map);
      this.trackingPolylines.push(polyline);
      (polylines[i].path || []).forEach((point) => {
        const validPoint = asMapCoordinate(point);
        if (validPoint) {
          bounds.extend(validPoint);
        }
      });

      this.addPolylineClickListener(google, polyline, polylines[i]);
    }

    if (fitBounds && !bounds.isEmpty()) {
      google.map.fitBounds(bounds, 48);
    } else if (highlightPoint || asMapCoordinate(last_posi)) {
      google.map.setCenter(highlightPoint || asMapCoordinate(last_posi));
      const zoom = Number(this.props.trackingMapZoom);
      google.map.setZoom(Number.isFinite(zoom) ? zoom : 16);
    }
  };

  componentWillUnmount() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
    }
    if (this.pathInfoWindowCloseTimer) {
      clearTimeout(this.pathInfoWindowCloseTimer);
      this.pathInfoWindowCloseTimer = null;
    }
  }

  componentDidUpdate(prevProps) {
    const highlightPoint = this.resolveHighlightMapPoint();
    const prevCenter = this.resolveMapCenter(prevProps.map, prevProps.landplots);
    const nextCenter = this.resolveMapCenter(this.props.map, this.props.landplots);
    if (
      !highlightPoint &&
      (prevCenter.lat !== nextCenter.lat ||
        prevCenter.lng !== nextCenter.lng)
    ) {
      this.setState({ center: nextCenter });
      const google = window.google;
      if (google && google.map) {
        google.map.setCenter(nextCenter);
      }
    }

    const prevLive = asMapCoordinate(prevProps.livePosition);
    const nextLive = asMapCoordinate(this.props.livePosition);
    if (
      nextLive &&
      (!prevLive || prevLive.lat !== nextLive.lat || prevLive.lng !== nextLive.lng)
    ) {
      if (this.state.marker) {
        this.state.marker.setPosition(nextLive);
      } else if (this.props.equipment && window.google && window.google.map) {
        this.state.marker = new google.maps.Marker({
          position: nextLive,
          title:
            this.props.livePosition && this.props.livePosition.speed,
          icon: require(`../assets/traccar/${this.props.equipment.machine_status}/${this.props.equipment.traccar_ico}.svg`),
          opacity: 1,
        });
        this.state.marker.setMap(window.google.map);
      }
    }

    if (
      this.state.marker &&
      this.props.equipment &&
      prevProps.equipment &&
      (prevProps.equipment.machine_status !== this.props.equipment.machine_status ||
        prevProps.equipment.traccar_ico !== this.props.equipment.traccar_ico)
    ) {
      this.state.marker.setIcon(
        require(`../assets/traccar/${this.props.equipment.machine_status}/${this.props.equipment.traccar_ico}.svg`)
      );
    }

    const google = window.google;
    if (!google || !google.map) return;

    const prevSignature = this.buildLinesSignature(
      prevProps.map && prevProps.map.lines
    );
    const nextSignature = this.buildLinesSignature(
      this.props.map && this.props.map.lines
    );
    const landplotsChanged = prevProps.landplots !== this.props.landplots;
    const highlightPointKey = (point) => {
      if (!point) return "";
      const lat = Number(point.lat);
      const lng = Number(point.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
      return `${lat},${lng}`;
    };
    const highlightChanged =
      highlightPointKey(prevProps.highlightMapPoint) !==
      highlightPointKey(this.props.highlightMapPoint);

    if (highlightChanged && highlightPoint) {
      this.setState({ center: highlightPoint });
    }

    if (
      nextSignature !== prevSignature ||
      (nextSignature !== this.trackingLinesSignature && nextSignature !== "0") ||
      highlightChanged
    ) {
      this.refreshTrackingOverlays(google, {
        fitBounds: this.props.fitTrackingBounds !== false,
      });
    } else if (landplotsChanged) {
      (this.landplotPolygons || []).forEach((polygon) => polygon.setMap(null));
      (this.landplotMarkers || []).forEach((marker) => marker.setMap(null));
      this.landplotPolygons = [];
      this.landplotMarkers = [];
      this.setFarmLandplots(google);
    }

    if (
      prevProps.directionMarkerClearToken !== this.props.directionMarkerClearToken &&
      this.directionMarkerRef
    ) {
      this.directionMarkerRef.setMap(null);
      this.directionMarkerRef = null;
    }
  }

  addMyLocationButton = (google) => {
    const controlDiv = document.createElement("div");
    controlDiv.style.margin = "10px";
    controlDiv.style.cursor = "pointer";

    const controlButton = document.createElement("button");
    controlButton.style.backgroundColor = "#fff";
    controlButton.style.border = "2px solid #fff";
    controlButton.style.borderRadius = "3px";
    controlButton.style.boxShadow = "0 2px 6px rgba(0,0,0,.3)";
    controlButton.style.width = "40px";
    controlButton.style.height = "40px";
    controlButton.style.cursor = "pointer";
    controlButton.style.display = "flex";
    controlButton.style.alignItems = "center";
    controlButton.style.justifyContent = "center";
    controlButton.style.outline = "none";
    controlButton.title = this.props.t
      ? this.props.t("Minha Localização")
      : "Minha Localização";
    controlButton.innerHTML =
      '<svg viewBox="0 0 24 24" width="24" height="24"><path fill="#666" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/></svg>';

    controlButton.addEventListener("click", () => {
      this.toggleUserLocation(google);
    });

    controlDiv.appendChild(controlButton);
    this.myLocationButton = controlButton;

    google.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(
      controlDiv
    );
  };

  toggleUserLocation = (google) => {
    if (this.state.userLocationActive) {
      // Desativar
      if (this.watchId !== null) {
        navigator.geolocation.clearWatch(this.watchId);
        this.watchId = null;
      }
      if (this.userLocationMarker) {
        this.userLocationMarker.setMap(null);
        this.userLocationMarker = null;
      }
      if (this.userLocationCircle) {
        this.userLocationCircle.setMap(null);
        this.userLocationCircle = null;
      }
      if (this.myLocationButton) {
        this.myLocationButton.style.backgroundColor = "#fff";
      }
      this.setState({ userLocationActive: false });
      return;
    }

    if (!navigator.geolocation) {
      alert(
        this.props.t
          ? this.props.t("Geolocalização não suportada pelo navegador")
          : "Geolocalização não suportada pelo navegador"
      );
      return;
    }

    if (this.myLocationButton) {
      this.myLocationButton.style.backgroundColor = "#e8f0fe";
    }
    this.setState({ userLocationActive: true });

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        const accuracy = position.coords.accuracy;

        if (!this.userLocationMarker) {
          // Criar marcador azul (ponto central)
          this.userLocationMarker = new google.maps.Marker({
            position: pos,
            map: google.map,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: "#4285F4",
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 2,
            },
            zIndex: 999,
          });

          // Criar círculo de precisão
          this.userLocationCircle = new google.maps.Circle({
            center: pos,
            radius: accuracy,
            map: google.map,
            fillColor: "#4285F4",
            fillOpacity: 0.15,
            strokeColor: "#4285F4",
            strokeOpacity: 0.3,
            strokeWeight: 1,
            clickable: false,
          });

          // Centralizar no usuário na primeira localização
          google.map.panTo(pos);
        } else {
          // Atualizar posição existente
          this.userLocationMarker.setPosition(pos);
          this.userLocationCircle.setCenter(pos);
          this.userLocationCircle.setRadius(accuracy);
        }
      },
      (error) => {
        console.error("Erro de geolocalização:", error);
        let msg = this.props.t
          ? this.props.t("Não foi possível obter sua localização")
          : "Não foi possível obter sua localização";
        if (error.code === 1) {
          msg = this.props.t
            ? this.props.t("Permissão de localização negada")
            : "Permissão de localização negada";
        }
        alert(msg);
        if (this.myLocationButton) {
          this.myLocationButton.style.backgroundColor = "#fff";
        }
        this.setState({ userLocationActive: false });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  handleGoogleMapApi = (google) => {
    // FERRAMENTAS DE DESENHO DO POLIGONO
    window.google = google;
    let drawingManager = null;
    try {
      drawingManager = new google.maps.drawing.DrawingManager({
        drawingControlOptions: {
          drawingModes: ["polygon", "polyline"],
          position: window.google.maps.ControlPosition.TOP_CENTER,
        },
        drawingControl: true,
        polylineOptions: {
          editable: true,
          strokeColor: "#00e600",
          fillColor: "#00e600",
          fillOpacity: 0.4,
          strokeWeight: 6,
        },
        polygonOptions: {
          editable: true,
          strokeColor: "#ff9933",
          fillColor: "#ff9933",
          fillOpacity: 0.4,
          strokeWeight: 5,
        },
      });

      drawingManager.setMap(google.map);
    } catch (e) {
      console.log("error", e);
    }
    if (drawingManager) {
      google.maps.event.addListener(
        drawingManager,
        "polylinecomplete",
        function (polyline) {
          let lengthInMeters = google.maps.geometry.spherical.computeLength(
            polyline.getPath()
          );
          alert(
            `O tamanho da linha em metros é de ${Math.round(lengthInMeters)} m`
          );

          function clearPolygon() {
            polyline.setMap(null);
          }

          google.maps.event.addDomListener(
            document.getElementById("CoordsButton"),
            "click",
            clearPolygon
          );
        }
      );

      // FUNÇÃO PARA TRABALHAR  OS DADOS DO POLIGONO
      google.maps.event.addListener(
        drawingManager,
        "polygoncomplete",
        function (polygon) {
          let path = polygon.getPath();

          //METROS
          let area = google.maps.geometry.spherical.computeArea(path);
          area = Math.trunc(area);

          //HECTARE
          let ha = area / 10000;
          alert(ha.toFixed(2) + " ha");

          drawingManager.setMap(null); //SETAR AS OPÇÕES DE DESENHO

          function clearPolygon() {
            polygon.setMap(null);
            drawingManager.setMap(google.map);
          }
          google.maps.event.addDomListener(
            document.getElementById("CoordsButton"),
            "click",
            clearPolygon
          );
        }
      );
    }

    const map = this.props.map || {};
    if (map.operation_type === "covering") {
      let path = [];
      if (map.geojson) {
        let geometry = JSON.parse(map.geojson);

        geometry.coordinates[0].map(function (item) {
          path.push({ lat: item[1], lng: item[0] });
          return { lat: item[1], lng: item[0] };
        });
      }
      // CONTRUIR O POLIGONO.
      let Polygon = new google.maps.Polygon({
        paths: [path],
        strokeColor: "#000000",
        strokeWeight: 5,
        fillColor: "#000000",
        fillOpacity: 0.4,
      });
      Polygon.setMap(google.map);
    } else {
      this.setFarmLandplots(google);
    }
    const fitBounds = this.props.fitTrackingBounds !== false;
    this.setPolyline(google, { fitBounds });
    if (!fitBounds && this.props.trackingMapZoom != null && google.map) {
      const zoom = Number(this.props.trackingMapZoom);
      if (Number.isFinite(zoom)) {
        google.map.setZoom(zoom);
        this.setState({ mapZoom: zoom });
      }
    }
    this.trackingLinesSignature = this.buildLinesSignature(
      this.props.map && this.props.map.lines
    );
    this.addMyLocationButton(google);
  }; //FIM HANDLE

  setFarmLandplots = (google) => {
    (this.props.landplots || []).forEach((lp) => {
      let geometry = JSON.parse(lp.geomap);
      let coords = geometry.coordinates[0].map(function (item) {
        return { lat: item[1], lng: item[0] };
      });
      let polygon = new google.maps.Polygon({
        paths: coords,
        strokeColor: LAND_PLOT_MAP_STYLE.strokeColor,
        strokeOpacity: LAND_PLOT_MAP_STYLE.strokeOpacity,
        strokeWeight: LAND_PLOT_MAP_STYLE.strokeWeight,
        fillColor: LAND_PLOT_MAP_STYLE.fillColor,
        fillOpacity: LAND_PLOT_MAP_STYLE.fillOpacity,
        clickable: false,
        zIndex: 1,
      });
      polygon.setMap(google.map);
      this.landplotPolygons.push(polygon);

      let marker = new google.maps.Marker({
        position: lp.landplot_center,
        label: { text: lp.name, color: "white" },
        clickable: false,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 0,
          labelOrigin: new google.maps.Point(0, 0),
        },
      });
      marker.setMap(google.map);
      this.landplotMarkers.push(marker);
    });
  };

  getMapOptions = (maps) => {
    return {
      streetViewControl: false,
      scaleControl: true,
      fullscreenControl: true,
      gestureHandling: "greedy",
      disableDoubleClickZoom: false,
      minZoom: 2,
      mapTypeId: maps.MapTypeId.SATELLITE,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: maps.ControlPosition.LEFT_TOP,
        mapTypeIds: [
          maps.MapTypeId.ROADMAP,
          maps.MapTypeId.SATELLITE,
          maps.MapTypeId.HYBRID,
        ],
      },
      zoomControl: true,
      clickableIcons: false,
      fullscreenControlOptions: { position: 1 },
    };
  };

  handleControllCollapse() {
    let { collapsePoints } = this.state;

    this.setState({
      collapsePoints: !collapsePoints,
    });
  }

  handleMarkerCenter = (e, data) => {
    e.preventDefault();

    let { lat, lng } = data;

    this.setState({
      center: { lat: lat, lng: lng },
      mapZoom: 30,
    });
  };

  TabSpeed() {
    return (
      <li
        className={`nav-item`}
        onClick={() =>
          this.setState({
            isActiveSpeeds: true,
            isActiveStops: false,
            isActiveLandplots: false,
          })
        }
      >
        <a
          className="nav-link active"
          id="speed-table"
          data-toggle="tab"
          href="#speed"
          role="tab"
          aria-controls="speed"
          aria-selected="true"
          onClick={() => this.methodMarkersSpeedData()}
        >
          {this.props.t("Velocidade")}
        </a>
      </li>
    );
  }

  methodMarkersSpeedData() {
    this.props.map.lines.map((item) => {
      this.renderMarkersSpeedData(item);
    });
  }

  renderMarkersSpeedData(item) {
    let google = window.google;

    let marker;
    let markers = [];
    let infowindow = new google.maps.InfoWindow();

    item.path.map((data) => {
      if (data.show === true) {
        marker = new google.maps.Marker({
          icon: {
            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            rotation: data.course,
            strokeColor: item.color,
            fillColor: item.color,
            scale: 3,
            strokeWeight: 3.5,
            fillOpacity: 0.9,
          },
          position: new google.maps.LatLng(data.lat, data.lng),
          map: google.map,
        });

        google.maps.event.addListener(
          marker,
          "click",
          (function (marker, i) {
            return function () {
              infowindow.setContent(
                `<div>
                    <strong style="font-weight: bold;">Velocidade:</strong> ${
                      data.speed
                    } <br>   
                    <br> 
                    <strong style="font-weight: bold;">Horário:</strong> ${formatTime(
                      data.time
                    )} min
                    <br>          
                </div>`
              );
              infowindow.open(google.map, marker);
            };
          })(marker)
        );

        markers.push(marker);

        function clearMarkers() {
          for (let i = 0; i < markers.length; i++) {
            markers[i].setMap(null);
          }
        }

        google.maps.event.addDomListener(
          document.getElementById("speed-table"),
          "click",
          clearMarkers
        );

        google.maps.event.addDomListener(
          document.getElementById("stops-tab"),
          "click",
          clearMarkers
        );

        google.maps.event.addDomListener(
          document.getElementById("clearPoints"),
          "click",
          clearMarkers
        );
      }
    });
  }

  TabsSpeedContent() {
    return (
      <div
        className="  table-responsive"
        id="speed"
        role="tabpanel"
        aria-labelledby="speed-table"
      >
        <table className="table">
          <thead className="thead-dark">
            <tr>
              <th scope="col">{this.props.t("Data/Hora")}</th>
              <th scope="col">{this.props.t("Velocidade")}</th>
            </tr>
          </thead>
          <tbody>
            {this.props.map.lines.map((data) =>
              this.returnRowsTableSpeedData(data)
            )}
          </tbody>
        </table>
      </div>
    );
  }

  returnRowsTableSpeedData(item) {
    return item.path.map((data, index) => {
      return data.show === true ? (
        <tr
          key={index}
          style={{
            fontSize: "13px",
            cursor: "pointer",
            backgroundColor: item.color,
          }}
          onClick={(e) => this.handleMarkerCenter(e, data)}
        >
          <td> {formatTime(data.time)}</td>
          <td> {data.speed}</td>
        </tr>
      ) : null;
    });
  }

  TabStops() {
    return (
      <li
        className={`nav-item `}
        onClick={() =>
          this.setState({
            isActiveStops: true,
            isActiveSpeeds: false,
            isActiveLandplots: false,
          })
        }
      >
        <a
          className="nav-link stopped-control"
          id="stops-tab"
          data-toggle="tab"
          href="#stops"
          role="tab"
          aria-controls="stops"
          aria-selected="false"
          onClick={() => this.setMarkersStoppedTime()}
        >
          {this.props.t("Paradas")}
        </a>
      </li>
    );
  }

  setMarkersStoppedTime() {
    let { map } = this.props;

    this.renderMarkersStoppedTime(map.stopped_points);
  }

  renderMarkersStoppedTime(item) {
    let google = window.google;
    let marker;
    let markers = [];
    let infowindow = new google.maps.InfoWindow();

    item.forEach((data) => {
      if (secondToMinute(data.stopped_time) >= 5) {
        marker = new google.maps.Marker({
          icon: data.status === "start" ? offlineIcon : stoppedIcon,
          position: new google.maps.LatLng(data.lat, data.lng),
          map: google.map,
        });

        google.maps.event.addListener(
          marker,
          "click",
          (function (marker, i) {
            return function () {
              infowindow.setContent(
                `<div>
                    <strong style="font-weight: bold;">Equipamento:</strong> ${
                      data.ident
                    } <br> 
                    <strong style="font-weight: bold;">Tempo:</strong> ${secondsToFormateTime(
                      data.stopped_time
                    )} min
                    <br> 
                    <strong style="font-weight: bold;">Horário:</strong> ${formatTime(
                      data.time
                    )}
                  </div>`
              );
              infowindow.open(google.map, marker);
            };
          })(marker)
        );

        markers.push(marker);

        function clearMarkers() {
          for (let i = 0; i < markers.length; i++) {
            markers[i].setMap(null);
          }
        }

        google.maps.event.addDomListener(
          document.getElementById("stops-tab"),
          "click",
          clearMarkers
        );

        google.maps.event.addDomListener(
          document.getElementById("speed-table"),
          "click",
          clearMarkers
        );

        google.maps.event.addDomListener(
          document.getElementById("clearPoints"),
          "click",
          clearMarkers
        );
      }
    });
  }

  TableStoppedPoints() {
    let { map } = this.props;

    return (
      <div
        className="table-responsive"
        id="stops"
        role="tabpanel"
        aria-labelledby="stops-tab"
      >
        <table className="table">
          <thead className="thead-dark">
            <tr>
              <th scope="col">{this.props.t("Data/Hora")}</th>
              <th scope="col">{this.props.t("P/D")}</th>
              <th scope="col">{this.props.t("Duração")}</th>
            </tr>
          </thead>
          <tbody>
            {map.stopped_points.map((data, index) => (
              <tr
                key={index}
                style={{ fontSize: "13px", cursor: "pointer" }}
                onClick={(e) => this.handleMarkerCenter(e, data)}
              >
                <td> {formatTime(data.time)}</td>
                <td> {data.status === "start" ? "D" : "P"}</td>
                <td> {secondsToFormateTime(data.stopped_time)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  TabLandplot() {
    return (
      <li
        className={`nav-item`}
        onClick={() =>
          this.setState({
            isActiveSpeeds: false,
            isActiveStops: false,
            isActiveLandplots: true,
          })
        }
      >
        <a
          className="nav-link active"
          id="landplot-table"
          data-toggle="tab"
          href="#landplot"
          role="tab"
          aria-controls="landplot"
          aria-selected="true"
          onClick={() => this.methodMarkersLandplots()}
        >
          {this.props.t("Talhão E/S")}
        </a>
      </li>
    );
  }
  methodMarkersLandplots() {
    this.props.map.lines.map((item) => {
      this.renderMarkerslandplots(item);
    });
  }

  renderMarkerslandplots(item) {
    let google = window.google;

    let marker;
    let markers = [];
    let infowindow = new google.maps.InfoWindow();

    item.path.map((data) => {
      if (data.io !== "") {
        marker = new google.maps.Marker({
          icon: data.io === "in" ? insideIcon : outIcon,
          position: new google.maps.LatLng(data.lat, data.lng),
          map: google.map,
        });

        google.maps.event.addListener(
          marker,
          "click",
          (function (marker, i) {
            return function () {
              infowindow.setContent(
                `<div>
                    <strong style="font-weight: bold;">Horário:</strong> ${formatTime(
                      data.time
                    )} min
                    <br>          
                </div>`
              );
              infowindow.open(google.map, marker);
            };
          })(marker)
        );

        markers.push(marker);

        function clearMarkers() {
          for (let i = 0; i < markers.length; i++) {
            markers[i].setMap(null);
          }
        }

        google.maps.event.addDomListener(
          document.getElementById("speed-table"),
          "click",
          clearMarkers
        );

        google.maps.event.addDomListener(
          document.getElementById("stops-tab"),
          "click",
          clearMarkers
        );

        google.maps.event.addDomListener(
          document.getElementById("clearPoints"),
          "click",
          clearMarkers
        );
      }
    });
  }

  TableLandplot() {
    return (
      <div
        className="table-responsive"
        id="landplot"
        role="tabpanel"
        aria-labelledby="landplot-tab"
      >
        <table className="table">
          <thead className="thead-dark">
            <tr>
              <th scope="col">{this.props.t("Data/Hora")}</th>
              <th scope="col">{this.props.t("E / S")}</th>
            </tr>
          </thead>
          <tbody>
            {this.props.map.lines.map((data) =>
              this.returnRowsTableLandplots(data)
            )}
          </tbody>
        </table>
      </div>
    );
  }

  returnRowsTableLandplots(item) {
    return item.path.map((data, index) => {
      return data.io !== "" ? (
        <tr
          key={index}
          style={{
            fontSize: "13px",
            cursor: "pointer",
            backgroundColor: data.io === "in" ? "#40bf40" : " #cc5200",
          }}
          onClick={(e) => this.handleMarkerCenter(e, data)}
        >
          <td> {formatTime(data.time)}</td>
          <td> {data.io === "in" ? "E" : "S"}</td>
        </tr>
      ) : null;
    });
  }

  verifyIsActiveTab() {
    this.setState({
      isActiveStops: false,
      isActiveSpeeds: false,
      isActiveLandplots: false,
    });
  }

  render() {
    const center = this.getEffectiveCenter();

    return (
      <div>
        <div className="map_style">
          <GoogleMapReact
            options={this.getMapOptions}
            bootstrapURLKeys={{
              libraries: ["drawing", "places", "geometry"],
              key: myConfig.GOOGLE_API_KEY,
            }}
            defaultCenter={center}
            center={center}
            zoom={this.state.mapZoom}
            yesIWantToUseGoogleMapApiInternals={true}
            onGoogleApiLoaded={this.handleGoogleMapApi.bind(this)}
            key={this.props.map_salt}
          />

          <button
            className="btn btn-sm btn-ligth collapsed-button-map "
            type="button"
            id="clearPoints"
            onClick={this.handleControllCollapse.bind(this)}
            style={{ borderRadius: "18px", width: "285px", height: "30px" }}
          >
            {this.state.collapsePoints === true ? (
              <p onClick={() => this.verifyIsActiveTab()}>
                {" "}
                {this.props.t("Esconder alertas")}
              </p>
            ) : (
              "Exibir alertas"
            )}
          </button>

          <Collapse isOpen={this.state.collapsePoints}>
            <div className="col collapsed-content" style={{ width: "285px" }}>
              <div className="x_content">
                <ul
                  className="nav nav-tabs bar_tabs"
                  role="tablist"
                  style={{ fontSize: "9px" }}
                >
                  {this.TabSpeed()}
                  {this.TabStops()}
                  {this.TabLandplot()}
                </ul>
                <div className="tab-content" id="">
                  {this.state.isActiveSpeeds && this.TabsSpeedContent()}
                  {this.state.isActiveStops && this.TableStoppedPoints()}
                  {this.state.isActiveLandplots && this.TableLandplot()}
                </div>
              </div>
            </div>
          </Collapse>

          <button id="CoordsButton" className="clear_map btn btn-default">
            {this.props.t("Limpar Área")}
          </button>
        </div>
      </div>
    );
  }
}

export default withTranslation()(MapContainer);
